import dayjs from 'dayjs';
import { CalendarViewOffices, WorkingHourInfo } from '../components/calendar-view/types';
import { PX_PER_MINUTE } from '../components/calendar-view/utils';

const hasOfficeHoursSet = (calendarData: CalendarViewOffices): boolean => {
  return calendarData.some((office) => office.hasAnyWorkingHoursSet || !!office.workingHours?.length);
};

const getOfficeWorkingHours = (calendarData: CalendarViewOffices): WorkingHourInfo[] => {
  for (const office of calendarData) {
    if (office.workingHours?.length) {
      return office.workingHours;
    }
  }
  return [];
};

const getWorkingHoursStart = (workingHours?: WorkingHourInfo[]): string | null => {
  if (!workingHours?.length) return null;

  // Find the earliest start time from all working hours
  const earliestHour = workingHours.reduce((earliest, current) => {
    if (!earliest) return current.startTime;
    return earliest;
  }, workingHours[0]?.startTime);

  return earliestHour || null;
};

const parseTimeString = (timeStr: string): dayjs.Dayjs => {
  let time = dayjs(timeStr, 'hh:mm A');
  if (!time.isValid()) time = dayjs(timeStr, 'HH:mm');
  if (!time.isValid()) time = dayjs(timeStr, 'h:mm A');
  return time;
};

const subtractMinutesFromTime = (timeStr: string, minutes: number): string => {
  const time = parseTimeString(timeStr);
  return time.subtract(minutes, 'minute').format('HH:mm');
};

const calculateScrollPositionForTime = (targetTime: string): number => {
  const time = parseTimeString(targetTime);

  if (!time.isValid()) {
    throw new Error(`Invalid time format: ${targetTime}`);
  }

  const targetHour = time.hour();
  const targetMinute = time.minute();
  // Calculate total minutes from start hour
  const totalMinutesFromStart = targetHour * 60 + targetMinute;
  // Convert to pixels (2px per minute based on calendar grid)
  const scrollPosition = totalMinutesFromStart * PX_PER_MINUTE;

  return scrollPosition;
};

const scrollToTime = (containerElement: HTMLElement | null, targetTime: string): void => {
  if (!containerElement) {
    return;
  }

  try {
    const scrollPosition = calculateScrollPositionForTime(targetTime);

    containerElement.scrollTo({
      top: scrollPosition,
      behavior: 'smooth',
    });
  } catch (error) {
    console.error('Failed to scroll to time:', error);
  }
};

export const scrollToPosition = (containerElement: HTMLElement | null, calendarData: CalendarViewOffices): void => {
  if (!containerElement || !calendarData?.length) return;

  try {
    let targetTime = '6:00'; // Default scroll to 6:00 AM
    const officeTimezone = calendarData[0]?.officeTimezone;
    const currentTimeObj = officeTimezone ? dayjs().tz(officeTimezone) : dayjs().tz();
    const hasOfficeHours = hasOfficeHoursSet(calendarData);
    const isTodayDateSelected = calendarData[0]?.calendarDateValue === currentTimeObj.format('YYYY-MM-DD');

    if (isTodayDateSelected) {
      targetTime = subtractMinutesFromTime(currentTimeObj.format('HH:mm'), 15);
    } else if (hasOfficeHours) {
      const workingHours = getOfficeWorkingHours(calendarData);
      if (workingHours.length) {
        const officeStart = getWorkingHoursStart(workingHours);
        if (officeStart) {
          targetTime = subtractMinutesFromTime(officeStart, 15);
        } else {
          targetTime = subtractMinutesFromTime(currentTimeObj.format('HH:mm'), 15);
        }
      }
    }

    console.info('Calendar scroll target time:', targetTime);
    // Execute the scroll
    scrollToTime(containerElement, targetTime);
  } catch (error) {
    console.error('Failed to execute calendar scroll:', error);
  }
};
